import { User2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface WhatsAppAvatarProps {
  nome?: string | null;
  /** Foto opcional do contato. Se presente, sobrepõe o ícone padrão. */
  fotoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'list';
  className?: string;
  /** Se passado, a foto se renova sozinha quando a URL expira (CDN do WhatsApp). */
  conversationId?: string;
}

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const SIZE_MAP = {
  sm: { box: 'h-8 w-8', icon: 'h-4 w-4' },
  // list = 38px. Cards do Inbox v3 (punch-list §2) — linha compacta de 66px.
  list: { box: 'h-[38px] w-[38px]', icon: 'h-4 w-4' },
  // md = 48px. Usado no header da ChatWindow. Padroniza com a lista
  // sem dominar o header (lg=64 ficaria grande demais ao lado do nome).
  // Douglas 24/05: "padronizar com os da lista de conversas".
  md: { box: 'h-12 w-12', icon: 'h-6 w-6' },
  // lg = 64px. Usado nos cards do Inbox — ocupa altura cheia da linha.
  lg: { box: 'h-16 w-16', icon: 'h-7 w-7' },
} as const;

/**
 * Avatar neutro do contato no WhatsApp Inbox.
 * - Se `fotoUrl` estiver disponível, mostra a foto redonda.
 * - Caso contrário (ou se a foto falhar ao carregar), mostra um ícone de pessoa.
 * As URLs de foto do WhatsApp (pps.whatsapp.net) EXPIRAM. Quando a imagem falha
 * e temos o `conversationId`, pedimos UMA URL nova pra edge whatsapp-fetch-avatar
 * (force) e trocamos — assim a foto "se autocura" ao abrir a conversa.
 */
export function WhatsAppAvatar({ nome, fotoUrl, size = 'md', className, conversationId }: WhatsAppAvatarProps) {
  const s = SIZE_MAP[size];
  const [url, setUrl] = useState<string | null | undefined>(fotoUrl);
  const [falhou, setFalhou] = useState(false);
  const jaTentouRef = useRef(false);

  // Quando a foto vinda das props muda, reseta o estado local.
  useEffect(() => {
    setUrl(fotoUrl);
    setFalhou(false);
    jaTentouRef.current = false;
  }, [fotoUrl]);

  const aoFalhar = async () => {
    // Sem conversationId ou já tentou renovar → mostra o boneco.
    if (jaTentouRef.current || !conversationId) { setFalhou(true); return; }
    jaTentouRef.current = true;
    try {
      const { data } = await supabase.functions.invoke('whatsapp-fetch-avatar', {
        body: { conversation_id: conversationId, force: true },
      });
      const nova = (data as any)?.avatar_url as string | null | undefined;
      if (nova && nova !== url) {
        setUrl(nova);
        setFalhou(false);
      } else {
        setFalhou(true);
      }
    } catch {
      setFalhou(true);
    }
  };

  if (url && !falhou) {
    return (
      <div
        className={cn(
          'relative flex-shrink-0 overflow-hidden rounded-full ring-1 ring-border bg-muted',
          s.box,
          className,
        )}
        title={nome || undefined}
      >
        <img
          src={url}
          alt={nome || 'Contato'}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={aoFalhar}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex-shrink-0 inline-flex items-center justify-center rounded-full',
        'bg-muted/70 ring-1 ring-border text-muted-foreground',
        'dark:bg-muted/30',
        s.box,
        className,
      )}
      title={nome || undefined}
      aria-label={nome || 'Contato'}
    >
      <User2 className={cn(s.icon)} strokeWidth={1.75} />
    </div>
  );
}

export { getInitials };
